// Right-click menu on a draft schedule's Gantt (owner, 2026-10-10): the
// day under the pointer becomes the activity's start, finish or sign-off
// date, and the activity can be renamed, placed after / before another, or
// deleted - the same edits the plan table makes, applied to the same draft
// rows, so Save schedule persists either the same way.
//
// Positioned in the chart root's own pixels (CoordinationGantt.toLocal),
// closed by a click anywhere else, Escape, or a scroll.
import React, { useEffect, useRef } from "react";

const MENU_W = 250;

export const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(+d) ? iso : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};

// What the menu offers for this activity at this day - pure, so it is tested
// without a chart. A row from the left table has no day: the three date
// actions are shown but disabled, and say why.
export function menuItems(item, date) {
  const day = fmtDay(date);
  const noDay = "Right-click the day on the chart";
  const signed = !!(item && item.signedOffAt);
  return [
    { key: "start", label: date ? `Make ${day} the start date` : "Make this the start date", disabled: !date, title: date ? "" : noDay },
    { key: "finish", label: date ? `Make ${day} the finish date` : "Make this the finish date", disabled: !date, title: date ? "" : noDay },
    {
      key: "signoff",
      label: date ? `Make ${day} the sign-off date` : "Make this the sign-off date",
      disabled: !date || signed,
      title: signed ? "Already signed off - withdraw the sign-off on the plan table first" : (date ? "The star moves here; a grey star keeps where it was first planned" : noDay),
    },
    { type: "divider" },
    { key: "rename", label: "Rename activity…" },
    { key: "after", label: "Place after activity…" },
    { key: "before", label: "Place before activity…" },
    { type: "divider" },
    { key: "delete", label: "Delete activity…", danger: true },
  ];
}

export default function GanttContextMenu({ menu, onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === "Escape") onClose(); };
    // Next tick: the right-click that opened the menu must not close it.
    const t = setTimeout(() => {
      document.addEventListener("mousedown", away, true);
      document.addEventListener("contextmenu", away, true);
      window.addEventListener("scroll", onClose, true);
    }, 0);
    document.addEventListener("keydown", key);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", away, true);
      document.removeEventListener("contextmenu", away, true);
      window.removeEventListener("scroll", onClose, true);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);
  const left = Math.max(0, Math.min(menu.x, (menu.w || 800) - MENU_W));
  const name = String((menu.item && menu.item.name) || "").trim();
  return (
    <div ref={ref} data-testid="gantt-context-menu" onContextMenu={(e) => e.preventDefault()}
      style={{ position: "absolute", left, top: menu.y, zIndex: 1050, width: MENU_W, background: "#fff", borderRadius: 8, boxShadow: "0 6px 16px rgba(0,0,0,0.12), 0 3px 6px -4px rgba(0,0,0,0.12)" }}>
      <div style={{ padding: "8px 12px 4px", fontSize: 12, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={name}>{name}</div>
      {menu.progressText ? <div data-testid="gantt-context-progress" style={{ padding: "0 12px 4px", fontSize: 12, color: "#555" }}>Progress: {menu.progressText}</div> : null}
      <div role="menu" style={{ padding: "0 4px 4px" }}>
        {menuItems(menu.item, menu.date).map((it, i) => (it.type
          ? <div key={`d${i}`} style={{ height: 1, background: "#f0f0f0", margin: "4px 8px" }} />
          : (
            <button key={it.key} type="button" role="menuitem" disabled={it.disabled} title={it.title || undefined}
              onClick={() => onPick(it.key)}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = "#f5f5f5"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{
                display: "block", width: "100%", textAlign: "left", border: "none", background: "transparent", borderRadius: 4,
                padding: "6px 10px", fontSize: 13, cursor: it.disabled ? "not-allowed" : "pointer",
                color: it.disabled ? "#bfbfbf" : (it.danger ? "#cf1322" : "#222"),
              }}>
              {it.label}
            </button>
          )))}
      </div>
    </div>
  );
}
